import { Injectable, inject } from '@angular/core';
import { Subject, Subscription, takeWhile } from 'rxjs';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { TextMessage, User } from 'devextreme/ui/chat';
import { ThomasAppConfig, ThomasCard, TrainingAssistantChatResponse } from './thomas-app.config';

export type ThomasPhase = 'thinking' | 'streaming' | 'done' | 'error';
export type ThomasDisplayMode = 'popup' | 'docked';

export interface ThomasFeedback {
  selection: boolean | null;
  comment: string;
  submitting: boolean;
  submitted: boolean;
  editing: boolean;
  savedSelection: boolean | null;
  savedComment: string;
}

// per-reply state; shared by reference with dxChat's template copies of the message
export interface ThomasReply {
  phase: ThomasPhase;
  responseId: string | null;
  cards: ThomasCard[];
  feedback: ThomasFeedback;
}

export interface ThomasMessage extends TextMessage {
  id: number;
  timestamp: Date;
  author: User;
  text: string;
  reply?: ThomasReply;
}

export const THOMAS_USER: User = { id: 'thomas', name: 'Thomas' };
export const CURRENT_USER: User = { id: 'me', name: 'You' };

const DEFAULT_STATUS = 'Working on your request...';
const MAX_MESSAGE_LENGTH = 4000;
const MAX_COMMENT_LENGTH = 2000;
const DISPLAY_MODE_KEY = 'thomas-display-mode';

// Owns the Thomas conversation (messages, streaming, feedback) so it
// survives the popup being closed and reopened mid-stream.
@Injectable({
  providedIn: 'root'
})
export class ThomasConversationService {

  readonly dataSource: DataSource<ThomasMessage, number>;
  readonly textUpdated = new Subject<void>();
  readonly conversationReset = new Subject<void>();

  open = false;
  busy = false;
  mode: ThomasDisplayMode = 'popup';

  private readonly store: CustomStore<ThomasMessage, number>;
  private messages: ThomasMessage[] = [];
  private nextId = 1;
  private pending: Subscription | null = null;
  private queue: { text: string; silent: boolean }[] = [];
  private lastResponseId: string | null = null;
  private lastChainToken: string | null = null;
  private conversationContext: string | null = null;
  private frame = 0;
  private dirty: ThomasMessage | null = null;

  private app = inject(ThomasAppConfig);

  constructor() {

    this.store = new CustomStore<ThomasMessage, number>({
      key: 'id',
      load: () => Promise.resolve([...this.messages]),
      // dxChat invokes insert before onMessageEntered even with reloadOnChange=false.
      // send() owns validation, IDs and insertion, including silent prompts.
      insert: message => Promise.resolve(message)
    });
    this.dataSource = new DataSource<ThomasMessage, number>({ store: this.store, paginate: false, pushAggregationTimeout: 0 });
    if (this.app.dockable && this.storedMode() === 'docked') {
      this.mode = 'docked';
    }

    // Chain tokens and visible history belong to the same user and company.
    this.app.contextChanged.subscribe(() => {
      if (!this.available || (this.conversationContext !== null && this.app.contextKey !== this.conversationContext)) {
        this.open = false;
        this.reset();
      }
    });
  }

  get available(): boolean {
    return this.app.available;
  }

  toggle() {
    this.open = this.available && !this.open;
  }

  get dockable(): boolean {
    return this.app.dockable && !this.app.phoneSize;
  }

  // switching shells rebuilds the chat, so the switches are hidden while the prompts panel is open
  managingPrompts = false;

  get docked(): boolean {
    return this.mode === 'docked' && this.dockable;
  }

  setMode(mode: ThomasDisplayMode) {
    this.mode = mode;
    try {
      localStorage.setItem(DISPLAY_MODE_KEY, mode);
    } catch {
      return;
    }
  }

  private storedMode(): string | null {
    try {
      return localStorage.getItem(DISPLAY_MODE_KEY);
    } catch {
      return null;
    }
  }

  // silent: the prompt goes to Thomas without a user bubble, so only the reply appears (suggestion chips)
  send(text: string, silent = false) {
    text = text?.trim();
    if (!text || !this.available) {
      return;
    }
    if (text.length > MAX_MESSAGE_LENGTH) {
      this.app.notifications.showWarning(`Messages are limited to ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    this.conversationContext ??= this.app.contextKey;
    if (this.busy) {
      this.queue.push({ text, silent });
    } else {
      this.ask(text, silent);
    }
  }

  get queuedCount(): number {
    return this.queue.length;
  }

  private ask(text: string, silent: boolean) {
    if (!silent) {
      this.insert(this.createMessage(CURRENT_USER, text));
    }
    const state = this.createReply();
    const reply = this.createMessage(THOMAS_USER, DEFAULT_STATUS, state);
    this.insert(reply);
    this.busy = true;

    let page: ReturnType<ThomasAppConfig['buildPage']>;
    try {
      page = this.app.buildPage();
    } catch (err) {
      this.fail(reply, this.app.global.returnError(err));
      return;
    }

    let answer = '';
    // Assign the owner before subscribing: completion may be synchronous (including errors).
    const pending = new Subscription();
    this.pending = pending;
    pending.add(this.app.transport
      .sendMessageStream(text, this.lastResponseId, this.lastChainToken, page)
      .pipe(takeWhile(event => event.event !== 'completed' && event.event !== 'error', true))
      .subscribe({
        next: event => {
          switch (event.event) {
            case 'status':
              if (state.phase === 'thinking') {
                this.updateText(reply, event.text || DEFAULT_STATUS);
              }
              break;
            case 'delta':
              state.phase = 'streaming';
              answer += event.text ?? '';
              this.updateText(reply, answer);
              break;
            case 'completed':
              if (event.response) {
                this.complete(reply, event.response);
              } else {
                this.fail(reply, 'The chat completed without a response.');
              }
              break;
            case 'error':
              this.fail(reply, event.text || 'Thomas is currently unavailable. Please try again later.');
              break;
          }
        },
        error: err => this.fail(reply, err instanceof Error ? err.message : this.app.global.returnError(err)),
        complete: () => {
          if (state.phase === 'thinking' || state.phase === 'streaming') {
            this.fail(reply, 'The chat stream was interrupted before it completed.');
          }
        }
      }));
  }

  reset() {
    this.pending?.unsubscribe();
    this.pending = null;
    this.cancelFrame();
    this.busy = false;
    this.queue = [];
    const removals = this.messages.map(message => ({ type: 'remove' as const, key: message.id }));
    this.messages = [];
    this.lastResponseId = null;
    this.lastChainToken = null;
    this.conversationContext = null;
    this.store.push(removals);
    this.conversationReset.next();
  }

  selectFeedback(reply: ThomasReply, isPositive: boolean) {
    const feedback = reply.feedback;
    if (feedback.submitting || (feedback.submitted && !feedback.editing)) {
      return;
    }
    feedback.selection = isPositive;
  }

  editFeedback(reply: ThomasReply) {
    const feedback = reply.feedback;
    if (!feedback.submitted || feedback.submitting) {
      return;
    }
    feedback.selection = feedback.savedSelection;
    feedback.comment = feedback.savedComment;
    feedback.editing = true;
  }

  cancelFeedback(reply: ThomasReply) {
    const feedback = reply.feedback;
    if (feedback.submitting) {
      return;
    }
    feedback.selection = feedback.submitted ? feedback.savedSelection : null;
    feedback.comment = feedback.submitted ? feedback.savedComment : '';
    feedback.editing = false;
  }

  submitFeedback(reply: ThomasReply) {
    const feedback = reply.feedback;
    if (!reply.responseId || feedback.selection === null || feedback.submitting) {
      return;
    }
    const comment = feedback.comment.trim() || null;
    if (comment && comment.length > MAX_COMMENT_LENGTH) {
      this.app.notifications.showWarning(`Feedback comments are limited to ${MAX_COMMENT_LENGTH} characters.`);
      return;
    }

    feedback.submitting = true;
    const wasSubmitted = feedback.submitted;
    const selection = feedback.selection;
    this.app.transport.submitFeedback(reply.responseId, selection, comment).subscribe({
      next: () => {
        feedback.submitting = false;
        feedback.submitted = true;
        feedback.editing = false;
        feedback.savedSelection = selection;
        feedback.savedComment = comment ?? '';
        this.app.notifications.showSuccess(wasSubmitted ? 'Feedback updated.' : 'Thanks for your feedback.');
      },
      error: err => {
        feedback.submitting = false;
        this.app.notifications.showError(this.app.global.returnError(err));
      }
    });
  }

  // the id of Thomas's answer to one of the user's messages, by id because dxChat's templates get copies
  replyIdFor(message: ThomasMessage): string | null {
    const index = this.messages.findIndex(m => m.id === message.id);
    return index < 0 ? null : this.messages.slice(index + 1).find(m => m.reply)?.reply?.responseId ?? null;
  }

  private createMessage(author: User, text: string, reply?: ThomasReply): ThomasMessage {
    return { id: this.nextId++, timestamp: new Date(), author, text, reply };
  }

  private createReply(): ThomasReply {
    return {
      phase: 'thinking',
      responseId: null,
      cards: [],
      feedback: { selection: null, comment: '', submitting: false, submitted: false, editing: false, savedSelection: null, savedComment: '' }
    };
  }

  private insert(message: ThomasMessage) {
    this.messages.push(message);
    this.store.push([{ type: 'insert', data: message }]);
  }

  // deltas arrive faster than the bubble should repaint, so text pushes are coalesced per frame
  private updateText(message: ThomasMessage, text: string) {
    message.text = text;
    this.dirty = message;
    if (!this.frame) {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.flushText();
      });
    }
  }

  private flushText() {
    if (this.dirty) {
      this.store.push([{ type: 'update', key: this.dirty.id, data: { text: this.dirty.text } }]);
      this.dirty = null;
      this.textUpdated.next();
    }
  }

  private cancelFrame() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.dirty = null;
  }

  // the completed response is authoritative: it replaces the streamed text and carries the cards
  private complete(message: ThomasMessage, response: TrainingAssistantChatResponse) {
    this.cancelFrame();
    const reply = message.reply!;
    reply.phase = 'done';
    reply.responseId = response.responseId;
    reply.cards = this.app.createCards(response);
    message.text = response.message;
    this.lastResponseId = response.responseId;
    this.lastChainToken = response.chainToken;
    this.finish(message);
  }

  private fail(message: ThomasMessage, error: string) {
    this.cancelFrame();
    message.reply!.phase = 'error';
    message.text = error;
    this.finish(message);
  }

  private finish(message: ThomasMessage) {
    this.pending?.unsubscribe();
    this.pending = null;
    this.dirty = message;
    this.flushText();
    const next = this.queue.shift();
    this.busy = false;
    if (next) {
      this.ask(next.text, next.silent);
    }
  }
}
